/**
 * * Using this hook to set and get items from local storage with optimzation.
 */

import { useState } from "react";

/**
 * @function useLocalStorage
 * @description Hook that is type generic and mimics the behavior of
 * useState Hook.
 * @returns an array of value of the provided key and [default value] and
 * a write function that sets the value of the provided key.
 */

interface IUseLocalStorage<T> {
  key: string;
  defaultValue?: T;
}

// Hook
export const useLocalStorage: (key: string, initialValue?: any) => any = (
  key,
  initialValue = null
) => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item != null ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      // eslint-disable-next-line no-console
      console.log(error);
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue: (value: any) => void = (value: any) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };
  return [storedValue, setValue];
};
